import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import React from "react";
import { NavigateFunction } from "react-router-dom";
import { PaginationParams } from "../clients/answeringClient";
import { Layout } from "../SharedComponents/Layout";
import { logout } from "../SharedComponents/LoginGuard/loginGuard";
import { Pagination } from "../SharedComponents/Pagination";
import { withNavigationHooks } from "../SharedComponents/withNavigationHooks";
import {
  getUserCrp,
  getUserRolesFromToken,
  RolesEnum,
} from "../utils/getUserRolesFromToken";
import { calculateTotalPages } from "../utils/pagination";
import CartModal, { CartModalDelegate } from "./CartModal/CartModal";
import InstrumentDiscountDialog from "./InstrumentDiscountDialog";
import { InstrumentDiscountEditorModal } from "./InstrumentDiscountEditorModal";
import {
  InstrumentDiscount,
  IInstrument,
  ManagedInstrumentsController,
  ManagedInstrumentsControllerDelegate,
} from "./controller";
import ManagedInstrumentRow, {
  ManagedInstrumentRowDelegate,
} from "./ManagedInstrumentRow";
import { SearchInput } from "../SharedComponents/SearchInput";
import { trackGtagEvent } from "../utils/analytics";

interface State {
  error: {};
  data: {
    instruments: IInstrument[];
    selectedInstrument?: IInstrument;
  };
  openedCartModal: boolean;
  isLoading: boolean;
  anchorEl: null | HTMLElement;
  discountDialogOpen: boolean;
  discountDialogLoading: boolean;
  discountDialogInstrument?: IInstrument;
  discountDialogError: boolean;
  instrumentDiscounts: InstrumentDiscount[];
  discountEditorInstrument?: IInstrument;
}

interface Props {
  navigationHooks: NavigateFunction;
}

class ManagedInstruments
  extends React.Component<Props, State>
  implements
  ManagedInstrumentsControllerDelegate,
  CartModalDelegate,
  ManagedInstrumentRowDelegate {
  private controller: ManagedInstrumentsController;
  private filters = {
    filter: "",
    page: 1,
    total: 1,
    limit: 9,
  };
  private lastTrackedSearchTerm: string | null = null;

  constructor(props: Props) {
    super(props);
    this.controller = new ManagedInstrumentsController(this);
    this.state = {
      anchorEl: null,
      error: {},
      data: {
        instruments: [],
      },
      isLoading: true,
      openedCartModal: false,
      discountDialogOpen: false,
      discountDialogLoading: false,
      discountDialogInstrument: undefined,
      discountDialogError: false,
      instrumentDiscounts: [],
      discountEditorInstrument: undefined,
    };
  }

  handleInfoClick = (event: React.MouseEvent<HTMLElement>) => {
    this.setState({ anchorEl: event.currentTarget });
  };

  handleInfoClose = () => {
    this.setState({ anchorEl: null });
  };

  pagination({ page, total }: { page: number; total: number }): void {
    throw new Error("Method not implemented.");
  }

  componentDidMount(): void {
    this.loadData();
  }

  // Delegate
  didGetInstruments(instruments: IInstrument[]): void {
    this.setState({
      isLoading: false,
      data: {
        ...this.state.data,
        instruments,
      },
    });
    const currentSearch = (this.filters.filter ?? "").trim();
    if (!currentSearch) {
      this.lastTrackedSearchTerm = null;
      return;
    }
    const normalizedSearch = currentSearch.toLowerCase();
    if (this.lastTrackedSearchTerm === normalizedSearch) {
      return;
    }
    this.trackSearchEvent(currentSearch, instruments.length);
    this.lastTrackedSearchTerm = normalizedSearch;
  }

  filter({
    page,
    total,
    filter,
  }: {
    page: number;
    total: number;
    filter: string;
  }): void {
    this.filters.page = page;
    this.filters.total = total;
    this.filters.filter = filter;
  }

  applicationShouldLogout(): void {
    logout();
  }

  genericError(): void { }

  shouldCloseModal() {
    this.setState({
      openedCartModal: false,
    });
  }

  didSelectInstrument(instrument: IInstrument): void {
    this.setState({
      openedCartModal: true,
      data: {
        ...this.state.data,
        selectedInstrument: instrument,
      },
    });
  }

  didRequestInstrumentDiscounts(instrument: IInstrument): void {
    this.setState({
      discountDialogOpen: true,
      discountDialogLoading: false,
      discountDialogInstrument: instrument,
      discountDialogError: false,
      instrumentDiscounts: instrument.discountRules ?? [],
    });
  }

  didRequestInstrumentDiscountEdit(instrument: IInstrument): void {
    this.setState({
      discountEditorInstrument: instrument,
    });
  }

  didGetInstrumentDiscounts(
    instrumentId: number,
    discounts: InstrumentDiscount[]
  ): void {
    if (this.state.discountDialogInstrument?.id !== instrumentId) {
      return;
    }

    this.setState({
      discountDialogLoading: false,
      discountDialogError: false,
      instrumentDiscounts: discounts,
    });
  }

  didFailToGetInstrumentDiscounts(instrumentId: number): void {
    if (this.state.discountDialogInstrument?.id !== instrumentId) {
      return;
    }

    this.setState({
      discountDialogLoading: false,
      discountDialogError: true,
    });
  }

  handleCloseDiscountDialog = () => {
    this.setState({
      discountDialogOpen: false,
      discountDialogLoading: false,
      discountDialogInstrument: undefined,
      discountDialogError: false,
      instrumentDiscounts: [],
    });
  };

  handleCloseDiscountEditor = () => {
    this.setState({
      discountEditorInstrument: undefined,
    });
  };

  // Internals
  loadData() {
    const token = localStorage.getItem("AccessToken")!;

    const newFilters: PaginationParams = {
      page: this.filters.page,
      limit: this.filters.limit,
      filter: this.filters.filter,
    };

    Object.entries(newFilters).forEach(([key, value]) => {
      if (value === "") {
        delete newFilters[key as keyof PaginationParams];
      }
    });

    this.controller.getManagedInstruments(token, newFilters);
  }

  private trackSearchEvent(searchString: string, resultsCount: number) {
    if (
      typeof window === "undefined" ||
      typeof document === "undefined" ||
      !document.cookie.includes("catvanteAnalyticsCookiesAccepted=true")
    ) {
      return;
    }

    trackGtagEvent("search", {
      search_term: searchString,
      results_count: resultsCount,
      content_category: "instrument",
    });

    const fbq = window.fbq;
    if (fbq) {
      fbq("track", "Search", {
        search_string: searchString,
        content_category: "instrument",
        results_count: resultsCount,
      });
    }
  }

  handlePageChange = (page: number) => {
    const totalPages =
      calculateTotalPages(this.filters.total, this.filters.limit) || 1;
    const nextPage = Math.min(Math.max(page, 1), totalPages);
    if (nextPage === this.filters.page) {
      return;
    }
    this.filters.page = nextPage;
    this.loadData();
  };

  renderLoading() {
    return (
      <Box flex={1} display="flex" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  renderInstrumentCards() {
    const canManageDiscounts = getUserRolesFromToken().includes(
      RolesEnum.ADMIN
    );

    const instruments = this.state.data.instruments;

    if (!instruments.length) {
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={200}
        >
          <Typography variant="body1" color="text.secondary">
            Nenhum instrumento encontrado.
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={3} alignItems="stretch">
        {instruments.map((instrument, id) => (
          <ManagedInstrumentRow
            instrument={instrument}
            key={`instrument_card_${instrument.id ?? id}`}
            delegate={this}
            canManageDiscounts={canManageDiscounts}
          />
        ))}
      </Grid>
    );
  }

  renderInstrumentsGrid() {
    const open = Boolean(this.state.anchorEl);
    const id = open ? "info-popover" : undefined;

    const userRoles = getUserRolesFromToken();
    const userCrp = getUserCrp();
    const verifiedPsychologist =
      userRoles.includes(RolesEnum.PSYCHOLOGIST) && userCrp != null;
    const userIsReseller = userRoles.includes(RolesEnum.RESELLER);

    return (
      <Box
        flex={1}
        overflow="auto"
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        <Container
          maxWidth="lg"
          style={{
            padding: "80px 20px",
          }}
        >
          <Box
            display={"flex"}
            justifyContent={"space-between"}
            alignItems={"center"}
            marginBottom={3}
            width={"100%"}
            flexWrap="wrap"
            gap={2}
          >
            <Stack direction="column" spacing={1.25}>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                flexWrap="wrap"
              >
                <Typography variant="h2">Instrumentos</Typography>
                <Chip
                  label={`${this.filters?.total} ${this.filters?.total !== 1 ? "encontrados" : "encontrado"}`}
                  size="small"
                />
              </Box>

              {userIsReseller ? (
                <Typography variant="subtitle1" color="text.secondary">
                  Acesso como revenda
                </Typography>
              ) : verifiedPsychologist ? (
                <Typography variant="subtitle1" color="text.secondary">
                  Acesso como aplicador(a) psicólogo(a)
                </Typography>
              ) : (
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="subtitle1" color="text.secondary">
                    Acesso como aplicador(a)
                  </Typography>
                  <IconButton
                    aria-describedby={id}
                    size="small"
                    onClick={this.handleInfoClick}
                    aria-label="informação"
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>

                  <Popover
                    id={id}
                    open={open}
                    anchorEl={this.state.anchorEl}
                    onClose={this.handleInfoClose}
                    anchorOrigin={{
                      vertical: "center",
                      horizontal: "right",
                    }}
                    transformOrigin={{
                      vertical: "center",
                      horizontal: "left",
                    }}
                  >
                    <Box
                      p={2}
                      maxWidth={300}
                      display="flex"
                      flexDirection="column"
                      alignItems="flex-start"
                      gap={1}
                    >
                      <Typography variant="body1" mb={1}>
                        Abaixo estão listados todos os instrumentos de acesso
                        aberto, exceto os testes psicológicos que são restritos
                        a psicólogos
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          window.open(
                            "https://wa.me/5511920742258?text=Sou%20psic%C3%B3logo(a)%20e%20quero%20ter%20acesso%20aos%20testes%20psicol%C3%B3gicos",
                            "_blank"
                          );
                          this.handleInfoClose();
                        }}
                      >
                        Sou psicólogo(a) e quero ter acesso aos testes
                        psicológicos
                      </Button>
                    </Box>
                  </Popover>
                </Box>
              )}
            </Stack>

            <SearchInput
              label="Pesquisar Instrumentos"
              onSearch={() => {
                this.filters.page = 1;
                this.loadData();
              }}
              onChangeFilter={(filter) => {
                this.filters.filter = filter;
              }}
            />
          </Box>

          <Box mt={4}>{this.renderInstrumentCards()}</Box>

          <Pagination
            currentPage={this.filters.page}
            totalPages={calculateTotalPages(
              this.filters.total,
              this.filters.limit
            )}
            onChangePage={this.handlePageChange}
          />
        </Container>
      </Box>
    );
  }

  renderDiscountEditorModal() {
    const { discountEditorInstrument } = this.state;

    const userRoles = getUserRolesFromToken();
    const isAdmin = userRoles.includes(RolesEnum.ADMIN);

    if (!isAdmin) {
      return null;
    }

    const handleSaved = (discounts: InstrumentDiscount[]) => {
      this.setState((prevState) => {
        const instrumentId = prevState.discountEditorInstrument?.id;
        if (!instrumentId) {
          return null;
        }

        const instruments = prevState.data.instruments.map((instrument) =>
          instrument.id === instrumentId
            ? { ...instrument, discountRules: discounts }
            : instrument
        );

        const selectedInstrument =
          prevState.data.selectedInstrument?.id === instrumentId
            ? {
                ...prevState.data.selectedInstrument,
                discountRules: discounts,
              }
            : prevState.data.selectedInstrument;

        const updatedDiscountDialogInstrument =
          prevState.discountDialogInstrument?.id === instrumentId
            ? {
                ...prevState.discountDialogInstrument,
                discountRules: discounts,
              }
            : prevState.discountDialogInstrument;

        const updatedDiscountEditorInstrument =
          prevState.discountEditorInstrument?.id === instrumentId
            ? {
                ...prevState.discountEditorInstrument,
                discountRules: discounts,
              }
            : prevState.discountEditorInstrument;

        return {
          instrumentDiscounts:
            updatedDiscountDialogInstrument?.id === instrumentId
              ? discounts
              : prevState.instrumentDiscounts,
          data: {
            ...prevState.data,
            instruments,
            selectedInstrument,
          },
          discountDialogInstrument: updatedDiscountDialogInstrument,
          discountEditorInstrument: updatedDiscountEditorInstrument,
        };
      });
    };

    return (
      <InstrumentDiscountEditorModal
        open={Boolean(discountEditorInstrument)}
        instrument={discountEditorInstrument}
        onClose={this.handleCloseDiscountEditor}
        onSaved={handleSaved}
      />
    );
  }

  render() {
    return (
      <div
        style={{
          display: "flex",
          height: "100vh",
        }}
      >
        {/* <Header logoutEnabled={true}/> */}
        <Layout>
          {this.state.isLoading
            ? this.renderLoading()
            : this.renderInstrumentsGrid()}
          {this.state.openedCartModal && (
            <CartModal
              delegate={this}
              instrument={this.state.data.selectedInstrument!}
            />
          )}
          <InstrumentDiscountDialog
            open={this.state.discountDialogOpen}
            instrument={this.state.discountDialogInstrument}
            loading={this.state.discountDialogLoading}
            error={this.state.discountDialogError}
            discounts={this.state.instrumentDiscounts}
            onClose={this.handleCloseDiscountDialog}
          />
          {this.renderDiscountEditorModal()}
        </Layout>
      </div>
    );
  }
}

export default withNavigationHooks(ManagedInstruments);
